import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../utils/cn'
import { motion, AnimatePresence } from 'framer-motion';

interface MobileMetricCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  subtitle?: string;
  trend?: {
    value: number;
    direction: 'up' | 'down';
    label: string;
  };
  details?: Array<{
    label: string;
    value: string | number;
    color?: string;
  }>;
  status?: 'success' | 'warning' | 'error' | 'info';
  className?: string;
}

export function MobileMetricCard({
  title,
  value,
  icon: Icon,
  subtitle,
  trend,
  details,
  status = 'info',
  className,
}: MobileMetricCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const statusColors = {
    success: 'border-green-500/20 bg-green-500/5',
    warning: 'border-yellow-500/20 bg-yellow-500/5',
    error: 'border-red-500/20 bg-red-500/5',
    info: 'border-blue-500/20 bg-blue-500/5',
  };

  const trendColors = {
    up: trend?.direction === 'up' && trend.value > 0 ? 'text-green-500' : 'text-red-500',
    down: trend?.direction === 'down' && trend.value < 0 ? 'text-green-500' : 'text-red-500',
  };

  return (
    <motion.div
      layout
      className={cn(
        'bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden',
        'transition-all duration-200 hover:border-gray-700',
        statusColors[status],
        className
      )}
    >
      {/* Primary Content - Always Visible */}
      <button
        onClick={() => details && setIsExpanded(!isExpanded)}
        className={cn(
          'w-full p-4 text-left',
          details && 'cursor-pointer active:bg-gray-800/50',
          'focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-inset'
        )}
        disabled={!details}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <h3 className="text-sm font-medium text-gray-300 truncate">{title}</h3>
            </div>
            <div className="text-2xl font-bold text-white mb-1">{value}</div>
            {subtitle && (
              <p className="text-xs text-gray-500">{subtitle}</p>
            )}
          </div>
          
          <div className="flex flex-col items-end gap-2">
            {trend && (
              <div className={cn('flex items-center gap-1', trendColors[trend.direction])}>
                {trend.direction === 'up' ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                <span className="text-sm font-medium">{trend.value}%</span>
              </div>
            )}
            {details && (
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="text-gray-400"
              >
                <ChevronDown className="w-5 h-5" />
              </motion.div>
            )}
          </div>
        </div>
      </button>

      {/* Expandable Details */}
      <AnimatePresence initial={false}>
        {isExpanded && details && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-2 border-t border-gray-800/50">
              <div className="space-y-2">
                {details.map((detail, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">{detail.label}</span>
                    <span 
                      className="font-medium"
                      style={{ color: detail.color || '#fff' }}
                    >
                      {detail.value}
                    </span>
                  </div>
                ))}
              </div>
              {trend && (
                <div className="mt-3 pt-3 border-t border-gray-800/50">
                  <p className="text-xs text-gray-500">{trend.label}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}